/**
 * TransactionsSubTab - TASK-229/TASK-238/TASK-255
 * Transaction Monitoring with backend integration, CSV import, and risk visualization
 * Enhanced with AML Sentinel integration
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  ArrowUpRight,
  ArrowDownLeft,
  AlertTriangle,
  CheckCircle,
  Download,
  Filter,
  Settings,
  TrendingUp,
  TrendingDown,
  FileSpreadsheet,
  Loader2,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { analyzeTransactionsBatch, AMLTransaction } from '../../../services/amlService';

interface Transaction {
  id: string;
  date: string;
  type: 'wire_in' | 'wire_out' | 'internal' | 'fee';
  amount: number;
  currency: string;
  counterparty: string;
  counterpartyCountry: string;
  account: string;
  status: 'clear' | 'alert' | 'blocked' | 'pending';
  reference: string;
  riskScore?: number;
  riskLevel?: string;
  riskReasoning?: string;
}

interface TransactionAlert {
  id: string;
  transactionId: string;
  amount: number;
  currency: string;
  direction: 'inbound' | 'outbound';
  counterparty: string;
  counterpartyCountry: string;
  reference: string;
  alertReasons: string[];
  timestamp: string;
  riskScore?: number;
}

// Mock data for initial display
const mockSummary = {
  totalTransactions: 12,
  totalInflows: 150000,
  totalOutflows: 275000,
  inflowChange: 25,
  outflowChange: 180,
  pendingAlerts: 1,
  resolvedAlerts: 3,
  currency: 'CHF',
};

const mockAlerts: TransactionAlert[] = [
  {
    id: 'alert-001',
    transactionId: 'txn-001',
    amount: 250000,
    currency: 'CHF',
    direction: 'outbound',
    counterparty: 'Smith Family Trust',
    counterpartyCountry: 'Germany',
    reference: 'Trust distribution Q4',
    alertReasons: [
      'Exceeds single transaction threshold (CHF 100,000)',
      'First transaction to this beneficiary',
      '180% above average monthly outflow',
    ],
    timestamp: '2024-11-28T16:45:00Z',
    riskScore: 75,
  },
];

const mockTransactions: Transaction[] = [
  {
    id: 'txn-001',
    date: '2024-11-28',
    type: 'wire_out',
    amount: 250000,
    currency: 'CHF',
    counterparty: 'Smith Family Trust',
    counterpartyCountry: 'DE',
    account: 'UBS Main',
    status: 'alert',
    reference: 'Trust distribution Q4',
    riskScore: 75,
    riskLevel: 'High',
  },
  {
    id: 'txn-002',
    date: '2024-11-25',
    type: 'wire_in',
    amount: 50000,
    currency: 'CHF',
    counterparty: 'ABC Corp',
    counterpartyCountry: 'CH',
    account: 'UBS Main',
    status: 'clear',
    reference: 'Salary Nov 2024',
    riskScore: 15,
    riskLevel: 'Low',
  },
  {
    id: 'txn-003',
    date: '2024-11-20',
    type: 'wire_out',
    amount: 15000,
    currency: 'CHF',
    counterparty: 'Swiss Tax Authority',
    counterpartyCountry: 'CH',
    account: 'UBS Main',
    status: 'clear',
    reference: 'Q3 Tax Payment',
    riskScore: 5,
    riskLevel: 'Low',
  },
  {
    id: 'txn-004',
    date: '2024-11-15',
    type: 'wire_in',
    amount: 100000,
    currency: 'CHF',
    counterparty: 'Investment Account',
    counterpartyCountry: 'CH',
    account: 'Credit Suisse',
    status: 'clear',
    reference: 'Portfolio dividend',
    riskScore: 10,
    riskLevel: 'Low',
  },
  {
    id: 'txn-005',
    date: '2024-11-10',
    type: 'wire_out',
    amount: 10000,
    currency: 'CHF',
    counterparty: 'Luxury Motors AG',
    counterpartyCountry: 'CH',
    account: 'UBS Main',
    status: 'clear',
    reference: 'Lease payment',
    riskScore: 8,
    riskLevel: 'Low',
  },
];

const SummaryCard: React.FC<{
  title: string;
  value: string | number;
  subtext?: string;
  trend?: { value: number; direction: 'up' | 'down' };
  variant?: 'default' | 'positive' | 'negative' | 'warning';
}> = ({ title, value, subtext, trend, variant = 'default' }) => {
  const variantStyles = {
    default: 'text-gray-900',
    positive: 'text-green-600',
    negative: 'text-red-600',
    warning: 'text-amber-600',
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <p className="text-xs text-gray-500 font-medium uppercase">{title}</p>
      <p className={`text-2xl font-bold mt-1 ${variantStyles[variant]}`}>{value}</p>
      {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
      {trend && (
        <p
          className={`text-sm mt-2 flex items-center gap-1 ${trend.direction === 'up' ? 'text-green-600' : 'text-red-600'}`}
        >
          {trend.direction === 'up' ? (
            <TrendingUp className="w-4 h-4" />
          ) : (
            <TrendingDown className="w-4 h-4" />
          )}
          {trend.direction === 'up' ? '+' : ''}
          {trend.value}% vs prev month
        </p>
      )}
    </div>
  );
};

const AlertCard: React.FC<{
  alert: TransactionAlert;
  onApprove: () => void;
  onBlock: () => void;
  onRequestInfo: () => void;
}> = ({ alert, onApprove, onBlock, onRequestInfo }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="bg-white rounded-lg border-2 border-amber-200 p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <span className="font-medium text-gray-900">Large Transaction Alert</span>
          {alert.riskScore && (
            <span
              className={`text-xs px-2 py-0.5 rounded font-medium ${
                alert.riskScore > 70
                  ? 'bg-red-100 text-red-700'
                  : alert.riskScore > 40
                    ? 'bg-orange-100 text-orange-700'
                    : 'bg-green-100 text-green-700'
              }`}
            >
              Risk: {alert.riskScore}/100
            </span>
          )}
        </div>
        <span className="text-sm text-gray-500">{formatDate(alert.timestamp)}</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
        <div>
          <span className="text-gray-500">Amount:</span>
          <p className="font-semibold">
            {alert.currency} {alert.amount.toLocaleString()}
          </p>
        </div>
        <div>
          <span className="text-gray-500">Direction:</span>
          <p className="font-semibold capitalize">{alert.direction}</p>
        </div>
        <div>
          <span className="text-gray-500">To:</span>
          <p className="font-semibold">{alert.counterparty}</p>
        </div>
        <div>
          <span className="text-gray-500">Country:</span>
          <p className="font-semibold">{alert.counterpartyCountry}</p>
        </div>
      </div>

      <div className="bg-amber-50 rounded-lg p-3 mb-4">
        <p className="text-xs font-semibold text-amber-800 mb-2">Alert Reasons:</p>
        <ul className="text-xs text-amber-700 space-y-1">
          {alert.alertReasons.map((reason, i) => (
            <li key={i}>• {reason}</li>
          ))}
        </ul>
      </div>

      <div className="flex gap-2">
        <button
          onClick={onApprove}
          className="flex-1 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
        >
          Approve
        </button>
        <button
          onClick={onBlock}
          className="flex-1 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
        >
          Block
        </button>
        <button
          onClick={onRequestInfo}
          className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
        >
          Request Info
        </button>
      </div>
    </div>
  );
};

const TransactionRow: React.FC<{ transaction: Transaction }> = ({ transaction }) => {
  const formatAmount = (amount: number, currency: string) => {
    return `${currency} ${amount.toLocaleString()}`;
  };

  const statusBadge = {
    clear: 'bg-green-100 text-green-700',
    alert: 'bg-amber-100 text-amber-700',
    blocked: 'bg-red-100 text-red-700',
    pending: 'bg-gray-100 text-gray-700',
  };

  const typeIcon = transaction.type.includes('in') ? (
    <ArrowDownLeft className="w-4 h-4 text-green-600" />
  ) : (
    <ArrowUpRight className="w-4 h-4 text-red-600" />
  );

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 text-sm text-gray-900">{transaction.date}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {typeIcon}
          <span className="text-sm capitalize">{transaction.type.replace('_', ' ')}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-sm font-medium text-gray-900">
        {formatAmount(transaction.amount, transaction.currency)}
      </td>
      <td className="px-4 py-3">
        <div className="text-sm">
          <p className="text-gray-900">{transaction.counterparty}</p>
          <p className="text-xs text-gray-500">{transaction.counterpartyCountry}</p>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-500">{transaction.account}</td>
      <td className="px-4 py-3">
        {transaction.riskScore !== undefined ? (
          <div className="flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full ${
                transaction.riskScore > 70
                  ? 'bg-red-500'
                  : transaction.riskScore > 40
                    ? 'bg-orange-500'
                    : 'bg-green-500'
              }`}
            />
            <span className="text-sm font-medium text-gray-700">{transaction.riskScore}/100</span>
          </div>
        ) : (
          <span className="text-xs text-gray-400">-</span>
        )}
      </td>
      <td className="px-4 py-3">
        <span
          className={`text-xs px-2 py-1 rounded font-medium ${statusBadge[transaction.status]}`}
        >
          {transaction.status === 'clear' && <CheckCircle className="w-3 h-3 inline mr-1" />}
          {transaction.status === 'alert' && <AlertTriangle className="w-3 h-3 inline mr-1" />}
          {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
        </span>
      </td>
    </tr>
  );
};

export const TransactionsSubTab: React.FC = () => {
  const [alerts, setAlerts] = useState(mockAlerts);
  const [transactions, setTransactions] = useState(mockTransactions);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatAmount = (amount: number) => `CHF ${(amount / 1000).toFixed(0)}k`;
  const netFlow = mockSummary.totalInflows - mockSummary.totalOutflows;

  // Clear notifications after 5 seconds
  useEffect(() => {
    if (importSuccess || importError) {
      const timer = setTimeout(() => {
        setImportSuccess(null);
        setImportError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [importSuccess, importError]);

  // CSV parsing helper
  const splitCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(
          current
            .trim()
            .replace(/^"|"$/g, '')
            .replace(/""/g, '"')
        );
        current = '';
      } else {
        current += char;
      }
    }
    result.push(
      current
        .trim()
        .replace(/^"|"$/g, '')
        .replace(/""/g, '"')
    );

    return result;
  };

  const parseCSV = (text: string): AMLTransaction[] => {
    const lines = text.split(/\r\n|\n|\r/).filter((l) => l.trim().length > 0);

    if (lines.length < 2) {
      throw new Error('CSV file is empty or invalid');
    }

    const headers = splitCSVLine(lines[0].toLowerCase());
    const transactions: AMLTransaction[] = [];

    // Column index finder
    const findIdx = (keywords: string[]) => {
      return headers.findIndex((h) => keywords.some((k) => h.includes(k)));
    };

    const idIdx = findIdx(['transaction_id', 'id', 'ref']);
    const timeIdx = findIdx(['booking_datetime', 'timestamp', 'date', 'time']);
    const amountIdx = findIdx(['amount', 'value', 'amt', 'balance']);
    const currencyIdx = findIdx(['currency', 'curr', 'ccy']);
    const senderIdx = findIdx(['originator_name', 'sender', 'from', 'source', 'remitter']);
    const receiverIdx = findIdx(['beneficiary_name', 'receiver', 'to', 'destination']);
    const rCountryIdx = findIdx(['beneficiary_country', 'receiver_country', 'destination_country']);
    const typeIdx = findIdx(['product_type', 'type', 'category', 'method', 'channel']);

    if (amountIdx === -1) {
      throw new Error('Column "amount" not found');
    }
    if (senderIdx === -1) {
      throw new Error('Column "originator_name" or "sender" not found');
    }

    for (let i = 1; i < lines.length; i++) {
      const row = splitCSVLine(lines[i]);

      if (row.length < 2) continue;

      const amount = parseFloat(row[amountIdx]);
      if (isNaN(amount)) continue;

      const timestamp =
        timeIdx > -1 && row[timeIdx] && !isNaN(Date.parse(row[timeIdx]))
          ? row[timeIdx]
          : new Date().toISOString();

      const type = typeIdx > -1 ? row[typeIdx]?.toLowerCase() || 'internal' : 'internal';

      transactions.push({
        id: idIdx > -1 && row[idIdx] ? row[idIdx] : `CSV-${Date.now()}-${i}`,
        amount,
        currency: currencyIdx > -1 ? row[currencyIdx] || 'CHF' : 'CHF',
        counterparty: receiverIdx > -1 ? row[receiverIdx] || 'Unknown' : 'Unknown',
        counterpartyCountry: rCountryIdx > -1 ? row[rCountryIdx] || 'Unknown' : 'Unknown',
        date: timestamp,
        type: type.includes('out') ? 'outbound' : type.includes('in') ? 'inbound' : 'internal',
        clientName: row[senderIdx] || 'Unknown',
        status: 'pending',
      });
    }

    return transactions;
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;

    const file = e.target.files[0];
    setIsImporting(true);
    setImportError(null);
    setImportSuccess(null);

    try {
      const text = await file.text();
      const parsedTransactions = parseCSV(text);

      if (parsedTransactions.length === 0) {
        throw new Error('No valid transactions found in file');
      }

      // Send to backend for analysis
      const result = await analyzeTransactionsBatch(parsedTransactions);

      // Convert results to local transaction format
      const analyzedTransactions: Transaction[] = parsedTransactions.map((tx, idx) => ({
        id: tx.id,
        date: new Date(tx.date).toISOString().split('T')[0],
        type: tx.type === 'outbound' ? 'wire_out' : tx.type === 'inbound' ? 'wire_in' : 'internal',
        amount: tx.amount,
        currency: tx.currency,
        counterparty: tx.counterparty,
        counterpartyCountry: tx.counterpartyCountry,
        account: 'Imported',
        status:
          result.results[idx]?.riskScore > 70
            ? 'alert'
            : result.results[idx]?.riskScore > 40
              ? 'pending'
              : 'clear',
        reference: tx.reference || 'CSV Import',
        riskScore: result.results[idx]?.riskScore,
        riskLevel: result.results[idx]?.riskLevel,
        riskReasoning: result.results[idx]?.reasoning,
      }));

      // Add new transactions and create alerts for high-risk
      setTransactions((prev) => [...analyzedTransactions, ...prev]);

      // Create alerts for flagged transactions
      const newAlerts: TransactionAlert[] = analyzedTransactions
        .filter((tx) => tx.status === 'alert' && tx.riskScore)
        .map((tx) => ({
          id: `alert-${tx.id}`,
          transactionId: tx.id,
          amount: tx.amount,
          currency: tx.currency,
          direction: tx.type.includes('out') ? ('outbound' as const) : ('inbound' as const),
          counterparty: tx.counterparty,
          counterpartyCountry: tx.counterpartyCountry,
          reference: tx.reference,
          alertReasons: tx.riskReasoning ? [tx.riskReasoning] : ['High risk score detected'],
          timestamp: new Date().toISOString(),
          riskScore: tx.riskScore,
        }));

      if (newAlerts.length > 0) {
        setAlerts((prev) => [...newAlerts, ...prev]);
      }

      setImportSuccess(
        `Successfully imported ${analyzedTransactions.length} transactions. ${newAlerts.length} flagged for review.`
      );
    } catch (err) {
      console.error('CSV import failed:', err);
      setImportError(err instanceof Error ? err.message : 'Failed to import CSV');
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleApprove = (alertId: string) => {
    setAlerts(alerts.filter((a) => a.id !== alertId));
    console.log('Approved alert:', alertId);
  };

  const handleBlock = (alertId: string) => {
    console.log('Blocked alert:', alertId);
  };

  const handleRequestInfo = (alertId: string) => {
    console.log('Requested info for alert:', alertId);
  };

  // Prepare chart data
  const chartData = transactions
    .slice(0, 15)
    .map((t) => ({
      id: t.id,
      amount: t.amount / 1000,
      risk: t.riskScore || 0,
    }))
    .reverse();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Transaction Monitoring</h3>
          <p className="text-sm text-gray-500">November 2024</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="file"
            accept=".csv"
            ref={fileInputRef}
            className="hidden"
            onChange={handleFileUpload}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isImporting}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {isImporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4" />
            )}
            {isImporting ? 'Importing...' : 'Import CSV'}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Settings className="w-4 h-4" />
            Configure Rules
          </button>
        </div>
      </div>

      {/* Notifications */}
      {importError && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {importError}
        </div>
      )}
      {importSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-700 p-3 rounded-lg text-sm flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          {importSuccess}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          title="This Month"
          value={mockSummary.totalTransactions}
          subtext={`Net: ${formatAmount(netFlow)}`}
        />
        <SummaryCard
          title="Inflows"
          value={formatAmount(mockSummary.totalInflows)}
          trend={{ value: mockSummary.inflowChange, direction: 'up' }}
          variant="positive"
        />
        <SummaryCard
          title="Outflows"
          value={formatAmount(mockSummary.totalOutflows)}
          trend={{ value: mockSummary.outflowChange, direction: 'up' }}
          variant="negative"
        />
        <SummaryCard
          title="Alerts"
          value={alerts.length}
          subtext={`${mockSummary.resolvedAlerts} resolved this month`}
          variant={alerts.length > 0 ? 'warning' : 'default'}
        />
      </div>

      {/* Risk Score Chart */}
      {chartData.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Transaction Risk Scores</h4>
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <XAxis dataKey="id" hide />
                <YAxis hide domain={[0, 100]} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    color: '#fff',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: '12px',
                  }}
                  formatter={(value: number, name: string) => [
                    name === 'risk' ? `${value}/100` : `${value}k`,
                    name === 'risk' ? 'Risk Score' : 'Amount',
                  ]}
                />
                <Bar dataKey="risk" radius={[4, 4, 0, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.risk > 70 ? '#ef4444' : entry.risk > 40 ? '#f97316' : '#22c55e'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Pending Alerts */}
      {alerts.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            Pending Alerts ({alerts.length})
          </h4>
          <div className="space-y-4">
            {alerts.map((alert) => (
              <AlertCard
                key={alert.id}
                alert={alert}
                onApprove={() => handleApprove(alert.id)}
                onBlock={() => handleBlock(alert.id)}
                onRequestInfo={() => handleRequestInfo(alert.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Transaction Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h4 className="font-medium text-gray-900">Recent Transactions</h4>
          <div className="flex items-center gap-2">
            <button className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded">
              <Filter className="w-4 h-4" />
              Filters
            </button>
            <button className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded">
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Amount
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Counterparty
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Account
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Risk
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {transactions.map((transaction) => (
                <TransactionRow key={transaction.id} transaction={transaction} />
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 text-sm text-gray-500">
          Showing {transactions.length} transactions
        </div>
      </div>
    </div>
  );
};

export default TransactionsSubTab;
